/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ClinicalPrivilegeNotificationSubscriberListResponse } from '../models/clinical-privilege-notification-subscriber-list-response';
import { ClinicalPrivilegeNotificationSubscriberRequest } from '../models/clinical-privilege-notification-subscriber-request';
import { ClinicalPrivilegeNotificationSubscriberResponse } from '../models/clinical-privilege-notification-subscriber-response';
import { ClinicalPrivilegeNotificationSubscriberRole } from '../models/clinical-privilege-notification-subscriber-role';
import { CreateClinicalPrivilegeNotificationSubscribersRequest } from '../models/create-clinical-privilege-notification-subscribers-request';
import { GenericResponse } from '../models/generic-response';

/**
 * Clinical privilege workflow notification subscriber lists.
 */
@Injectable({ providedIn: 'root' })
export class ClinicalPrivilegeNotificationSubscribersService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `listClinicalPrivilegeNotificationSubscribers()` */
  static readonly ListClinicalPrivilegeNotificationSubscribersPath =
    '/api/v1/clinical-privileges/notification-subscribers';

  /**
   * List notification subscribers.
   *
   * **Sortable columns** — use `sort=column,asc` or `sort=column,desc`:
   * - `name` (default)
   * - `email`, `recipientRole`, `emailType`, `createdAt`, `updatedAt`, `id`
   *
   * **Search** — partial match, not case-sensitive:
   * - `name`, `email`
   *
   * **Filters:**
   * - `role` — one or more subscriber roles; omit to return all roles
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listClinicalPrivilegeNotificationSubscribers()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalPrivilegeNotificationSubscribers$Response(
    params?: {
      /**
       * Filter by subscriber role. Pass multiple values to select more than one role.
       */
      role?: Array<ClinicalPrivilegeNotificationSubscriberRole>;

      /**
       * Search subscriber name or email.
       */
      search?: string;

      /**
       * Sort order. Format: column,asc or column,desc. Default: name,asc.
       */
      sort?: string;
    },
    context?: HttpContext
  ): Observable<
    StrictHttpResponse<ClinicalPrivilegeNotificationSubscriberListResponse>
  > {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeNotificationSubscribersService.ListClinicalPrivilegeNotificationSubscribersPath,
      'get'
    );
    if (params) {
      rb.query('role', params.role, { style: 'form', explode: true });
      rb.query('search', params.search, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeNotificationSubscriberListResponse>;
        })
      );
  }

  /**
   * List notification subscribers.
   *
   * **Sortable columns** — use `sort=column,asc` or `sort=column,desc`:
   * - `name` (default)
   * - `email`, `recipientRole`, `emailType`, `createdAt`, `updatedAt`, `id`
   *
   * **Search** — partial match, not case-sensitive:
   * - `name`, `email`
   *
   * **Filters:**
   * - `role` — one or more subscriber roles; omit to return all roles
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listClinicalPrivilegeNotificationSubscribers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalPrivilegeNotificationSubscribers(
    params?: {
      /**
       * Filter by subscriber role. Pass multiple values to select more than one role.
       */
      role?: Array<ClinicalPrivilegeNotificationSubscriberRole>;

      /**
       * Search subscriber name or email.
       */
      search?: string;

      /**
       * Sort order. Format: column,asc or column,desc. Default: name,asc.
       */
      sort?: string;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeNotificationSubscriberListResponse> {
    return this.listClinicalPrivilegeNotificationSubscribers$Response(
      params,
      context
    ).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeNotificationSubscriberListResponse>
        ): ClinicalPrivilegeNotificationSubscriberListResponse => r.body
      )
    );
  }

  /** Path part for operation `createClinicalPrivilegeNotificationSubscribers()` */
  static readonly CreateClinicalPrivilegeNotificationSubscribersPath =
    '/api/v1/clinical-privileges/notification-subscribers';

  /**
   * Add notification subscribers.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClinicalPrivilegeNotificationSubscribers()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClinicalPrivilegeNotificationSubscribers$Response(
    params: {
      body: CreateClinicalPrivilegeNotificationSubscribersRequest;
    },
    context?: HttpContext
  ): Observable<
    StrictHttpResponse<ClinicalPrivilegeNotificationSubscriberListResponse>
  > {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeNotificationSubscribersService.CreateClinicalPrivilegeNotificationSubscribersPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeNotificationSubscriberListResponse>;
        })
      );
  }

  /**
   * Add notification subscribers.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createClinicalPrivilegeNotificationSubscribers$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClinicalPrivilegeNotificationSubscribers(
    params: {
      body: CreateClinicalPrivilegeNotificationSubscribersRequest;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeNotificationSubscriberListResponse> {
    return this.createClinicalPrivilegeNotificationSubscribers$Response(
      params,
      context
    ).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeNotificationSubscriberListResponse>
        ): ClinicalPrivilegeNotificationSubscriberListResponse => r.body
      )
    );
  }

  /** Path part for operation `deleteClinicalPrivilegeNotificationSubscriber()` */
  static readonly DeleteClinicalPrivilegeNotificationSubscriberPath =
    '/api/v1/clinical-privileges/notification-subscribers/{id}';

  /**
   * Delete notification subscriber.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteClinicalPrivilegeNotificationSubscriber()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClinicalPrivilegeNotificationSubscriber$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeNotificationSubscribersService.DeleteClinicalPrivilegeNotificationSubscriberPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete notification subscriber.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteClinicalPrivilegeNotificationSubscriber$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClinicalPrivilegeNotificationSubscriber(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteClinicalPrivilegeNotificationSubscriber$Response(
      params,
      context
    ).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateClinicalPrivilegeNotificationSubscriber()` */
  static readonly UpdateClinicalPrivilegeNotificationSubscriberPath =
    '/api/v1/clinical-privileges/notification-subscribers/{id}';

  /**
   * Update notification subscriber.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClinicalPrivilegeNotificationSubscriber()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClinicalPrivilegeNotificationSubscriber$Response(
    params: {
      id: number;
      body: ClinicalPrivilegeNotificationSubscriberRequest;
    },
    context?: HttpContext
  ): Observable<
    StrictHttpResponse<ClinicalPrivilegeNotificationSubscriberResponse>
  > {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeNotificationSubscribersService.UpdateClinicalPrivilegeNotificationSubscriberPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeNotificationSubscriberResponse>;
        })
      );
  }

  /**
   * Update notification subscriber.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateClinicalPrivilegeNotificationSubscriber$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClinicalPrivilegeNotificationSubscriber(
    params: {
      id: number;
      body: ClinicalPrivilegeNotificationSubscriberRequest;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeNotificationSubscriberResponse> {
    return this.updateClinicalPrivilegeNotificationSubscriber$Response(
      params,
      context
    ).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeNotificationSubscriberResponse>
        ): ClinicalPrivilegeNotificationSubscriberResponse => r.body
      )
    );
  }
}
